// @flow
import React, {useEffect, useState} from 'react';
import {
  makeStyles,
  Typography,
  Grid,
  InputAdornment,
  Divider,
} from '@material-ui/core';
import {useForm} from 'react-hook-form';
import {useParams, useHistory} from 'react-router-dom';
import CustomTextField from '../../../../../elements/CustomTextField/CustomTextField';
import {useStoreCountry} from '../../../../country/utils';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {colors} from '../../../../../assets/styles/colors';
import CustomExitPrompt from '../../../../../elements/CustomExitPrompt/CustomExitPrompt';
import {
  useCRUDStandardRate,
  useFetchShippingStandard,
  useGetCurrencyCountry,
  useGetShippingMethodActive,
  useResetState,
  useUpdateShippingMethodActive,
} from '../utils';
import ActivateShippingModal from '../components/ActivateShippingModal';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',
    borderLeft: `1px solid #dbdbe0`,
    borderRight: `1px solid #dbdbe0`,
    borderBottom: `1px solid #dbdbe0`,
  },
  contentBackground: {
    backgroundColor: '#f8f9fb',
    padding: theme.spacing(2),
    borderRadius: '12px',
    marginTop: theme.spacing(2),
  },
  greyText: {
    color: colors.grey600,
    fontSize: '0.75rem',
  },
  messageSpace: {
    marginBottom: theme.spacing(2, 0),
    alignSelf: 'stretch',
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  messageField: {
    flexGrow: 1,
    marginBottom: theme.spacing(1),
  },
  messageTips: {
    margin: theme.spacing(0, 3),
    marginInline: 0,
    [theme.breakpoints.down('md')]: {
      margin: 'initial',
    },
  },
  messageRow: {
    display: 'flex',
    alignItems: 'center',
    columnGap: theme.spacing(6),
  },
  bold: {
    fontWeight: 600,
  },
  saveButton: {
    marginTop: theme.spacing(2),
    width: 'fit-content',
    paddingInline: theme.spacing(8),
  },
  form: {
    paddingInline: theme.spacing(2),
    paddingBottom: '22px',
  },
}));

export default function LogisticShippingNational() {
  const classes = useStyles();
  const history = useHistory();
  const {adminRateId} = useParams();

  const [showModalActive, setShowModalActive] = useState(false);
  const [optionSave, setOptionSave] = useState('');
  const {register, errors, getValues, handleSubmit, formState} = useForm();
  const {isDirty} = formState;
  const country = useStoreCountry();
  const currency = useGetCurrencyCountry();
  const {updateShippingMethodActive} = useUpdateShippingMethodActive();
  const {
    shippingStandard,
    fetchShippingStandardData,
  } = useFetchShippingStandard();
  const {
    isSaving,
    savingStandardRateSuccess,
    createStandardRate,
    updateStandardRate,
  } = useCRUDStandardRate();
  const shippingMethod = useGetShippingMethodActive();
  const {resetState} = useResetState();

  const handleValidation = () => {
    if (
      shippingMethod.shippingMethodActive.storeShippingMethodSettings
        .storeShippingMethodType !== 'STANDARD'
    ) {
      setShowModalActive(true);
      return false;
    }
    return true;
  };

  const handleBeforeSubmit = () => {
    if (!handleValidation()) {
      return;
    }

    handleFormSubmit('save');
  };

  const handleFormSubmit = (optionSave: string) => {
    const data = getValues();

    let formattedRateRequest = {
      enabled: true,
      country: country.code,
      ...data,
      storeShippingMethodSubType: 'BASIC',
    };

    if (adminRateId) {
      updateStandardRate({
        standardRateId: adminRateId,
        standardRate: formattedRateRequest,
      });
    } else {
      createStandardRate(formattedRateRequest);
    }

    if (optionSave === 'saveAndActivate') {
      setOptionSave('saveAndActivate');
    }
  };

  useEffect(() => {
    if (savingStandardRateSuccess) {
      if (optionSave === 'saveAndActivate') {
        updateShippingMethodActive({
          shippingMethodType: 'STANDARD',
        });
      }
      history.push('/configure/logistics/shipping/standard');
    }
  }, [
    savingStandardRateSuccess,
    history,
    updateShippingMethodActive,
    optionSave,
  ]);

  useEffect(() => {
    if (adminRateId) {
      fetchShippingStandardData(adminRateId);
    }
  }, [adminRateId, fetchShippingStandardData]);

  useEffect(() => {
    return () => {
      resetState();
    };
  }, [resetState]);

  return (
    <div className={classes.root}>
      <CustomExitPrompt when={isDirty && !savingStandardRateSuccess} />
      <Divider />
      {!adminRateId && (
        <form
          onSubmit={handleSubmit(handleBeforeSubmit)}
          className={classes.form}
        >
          <div className={classes.contentBackground}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Nombre"
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="displayName"
                  error={!!errors.displayName}
                  helperText={
                    errors.displayName ? errors.displayName.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Costo de envío aproximado"
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    rows={5}
                    rowsMax={18}
                    label="Mensaje"
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="description"
                    error={!!errors.description}
                    helperText={errors.description ? errors.description : ''}
                    type="text"
                    inputRef={register}
                    disabled={isSaving}
                  />
                </div>
                <div className={classes.messageTips}>
                  <Typography className={classes.greyText} paragraph>
                    Es importante incluir información como:
                  </Typography>
                  <div className={classes.messageRow}>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Días de despacho
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Despachos los martes y jueves
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Rango de tiempo hasta la recepción del pedido
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Argentina: entre 24 a 48 horas | Brazil: 3 - 4 días
                        útiles
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
          <CustomButton
            variant="contained"
            color="primary"
            className={classes.saveButton}
            type="submit"
            disabled={isSaving}
          >
            Guardar
          </CustomButton>
        </form>
      )}

      {adminRateId && shippingStandard && (
        <form
          onSubmit={handleSubmit(handleBeforeSubmit)}
          className={classes.form}
        >
          <div className={classes.contentBackground}>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Nombre"
                  defaultValue={
                    shippingStandard ? shippingStandard.displayName : ''
                  }
                  InputProps={{
                    className: classes.textInput,
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="displayName"
                  error={!!errors.displayName}
                  helperText={
                    errors.displayName ? errors.displayName.message : ''
                  }
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="text"
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6} lg={6}>
                <CustomTextField
                  className={classes.textField}
                  label="Costo de envío aproximado"
                  defaultValue={shippingStandard ? shippingStandard.price : ''}
                  InputProps={{
                    className: classes.textInput,
                    startAdornment: (
                      <InputAdornment position="start">
                        {currency ? currency.symbol : ''}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{className: classes.textInputLabel}}
                  name="price"
                  error={!!errors.price}
                  helperText={errors.price ? errors.price.message : ''}
                  inputRef={register({
                    required: 'Campo obligatorio',
                  })}
                  type="number"
                  inputProps={{step: '.01'}}
                  disabled={isSaving}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <div className={classes.messageSpace}>
                  <CustomTextField
                    multiline
                    rows={5}
                    rowsMax={18}
                    label="Mensaje"
                    className={`${classes.textField} ${classes.messageField}`}
                    InputProps={{
                      className: classes.textInput,
                    }}
                    InputLabelProps={{className: classes.textInputLabel}}
                    name="description"
                    error={!!errors.description}
                    helperText={errors.description ? errors.description : ''}
                    defaultValue={
                      shippingStandard ? shippingStandard.description : ''
                    }
                    type="text"
                    inputRef={register}
                    // disabled={isSaving}
                  />
                </div>
                <div className={classes.messageTips}>
                  <Typography className={classes.greyText} paragraph>
                    Es importante incluir información como:
                  </Typography>
                  <div className={classes.messageRow}>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Días de despacho
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Despachos los martes y jueves
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        className={`${classes.greyText} ${classes.bold}`}
                      >
                        Rango de tiempo hasta la recepción del pedido
                      </Typography>
                      <Typography className={classes.greyText} paragraph>
                        Argentina: entre 24 a 48 horas | Brazil: 3 - 4 días
                        útiles
                      </Typography>
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
          <CustomButton
            variant="contained"
            color="primary"
            type="submit"
            className={classes.saveButton}
            disabled={isSaving}
          >
            Guardar
          </CustomButton>
        </form>
      )}

      <ActivateShippingModal
        open={showModalActive}
        handleClose={() => {
          setShowModalActive(false);
        }}
        typeRateSelected="STANDARD"
        handleFormSubmit={handleFormSubmit}
        isSaving={isSaving}
      />
    </div>
  );
}
